import { useState } from 'react';

import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import TelegramIcon from '@mui/icons-material/Telegram';
import { Alert, Button, Chip, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { testTelegramConnection } from '../../api/telegramSettingsApi';
import {
  useTelegramChannelSettings,
  useTelegramSettings,
  useUpdateTelegramChannelSettings,
  useUpdateTelegramSettings,
} from '../../hooks';
import {
  deliveryFieldError,
  toSettingsUpdate,
  useDomainPermissions,
  useFeedback,
  useSettingsForm,
} from '../../lib';
import type {
  TelegramChannelSettings,
  TelegramParseMode,
  UpdateTelegramChannelSettingsRequest,
} from '../../model/settingsTypes';
import { LoadingState } from '@/shared/ui';

import {
  ConfiguredSecretField,
  FeedbackSnackbar,
  FieldRow,
  MovedSettingsNotice,
  NumberField,
  SelectField,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
  TestConnectionButton,
  TextInputField,
} from '../components';

function buildTelegramChannelUpdate(
  draft: TelegramChannelSettings,
  botTokenCandidate: string,
  webhookSecretCandidate: string,
): UpdateTelegramChannelSettingsRequest {
  // botSecretConfigured is a read-only server indicator; botToken/webhookSecret are edited via
  // separate candidate state (below), never through the generic draft — see the page doc comment
  // on why (kept across a 409, per design-channel-connection-pages.md §5.2/§7).
  const update = toSettingsUpdate(draft) as Record<string, unknown>;
  delete update.botSecretConfigured;
  const token = botTokenCandidate.trim();
  const secret = webhookSecretCandidate.trim();
  update.botToken = token === '' ? null : token;
  update.webhookSecret = secret === '' ? null : secret;
  return update as UpdateTelegramChannelSettingsRequest;
}

/**
 * Telegram settings — connection + delivery only (plan-conversation-001 WP-MP.2, shrunk further in
 * plan-merchant-portal-019 WP2).
 *
 * Shrunk from the former 928-line, four-section page (design-channel-connection-pages.md): the
 * storefront business profile moved to `/settings/tenant`, and the six behaviour toggles moved
 * conceptually to Chatbot / Sales channels / Bot texts & FAQ. What is left is what this adapter is
 * the system of record for — bot identity, credentials, transport (adr-conversation-006 D4).
 *
 * **The interim "Chatbot behaviour (general)" bridge card that WP-MP.2 shipped here is retired.**
 * `/settings/chatbot` (plan-merchant-portal-019 WP2 = plan-conversation-001 WP-MP.4) is the real
 * screen now — General/Telegram/MAX per-card tabs, the availability strip, the AI provider card —
 * so the sequencing gap that bridge existed to close no longer exists. `MovedSettingsNotice` below
 * points at it instead of naming a temporary card.
 */
export function TelegramSettingsPage() {
  const { t } = useTranslation('settings');
  const PARSE_MODE_OPTIONS = [
    { value: 'HTML', label: t('telegramSettings.formattingSection.parseModeOptions.html') },
    {
      value: 'MarkdownV2',
      label: t('telegramSettings.formattingSection.parseModeOptions.markdownV2'),
    },
  ];

  // ── Permissions ──────────────────────────────────────────────────────────────
  const { canView, canManage, canManageCredentials } = useDomainPermissions('telegram');
  const chatbotPermissions = useDomainPermissions('chatbot');
  const templatesPermissions = useDomainPermissions('chatbot-templates');
  const channelsPermissions = useDomainPermissions('channels');
  const tenantPermissions = useDomainPermissions('tenant');

  // ── Queries & mutations ──────────────────────────────────────────────────────
  const channelQuery = useTelegramChannelSettings();
  const channelUpdate = useUpdateTelegramChannelSettings();
  const transportQuery = useTelegramSettings();
  const transportUpdate = useUpdateTelegramSettings();

  // ── Draft forms (independent per resource) ───────────────────────────────────
  const channelForm = useSettingsForm(channelQuery.data);
  const transportForm = useSettingsForm(transportQuery.data);

  // ── Delivery & limits validation (stage defect 27.09: a cleared/out-of-range field could reach
  // the adapter and 422 as INVALID_RANGE — see lib/deliveryFieldValidation.ts) ──────────────────
  const transportDraft = transportForm.draft;
  const maxRetriesError = transportDraft
    ? deliveryFieldError(
        t,
        'telegramSettings.deliverySection',
        transportDraft.deliveryMaxRetries,
        0,
        10,
      )
    : null;
  const retryBackoffError = transportDraft
    ? deliveryFieldError(
        t,
        'telegramSettings.deliverySection',
        transportDraft.deliveryRetryBackoffMs,
        0,
      )
    : null;
  const sendTimeoutError = transportDraft
    ? deliveryFieldError(
        t,
        'telegramSettings.deliverySection',
        transportDraft.sendTimeoutMs,
        100,
        120000,
      )
    : null;
  const rateLimitError = transportDraft
    ? deliveryFieldError(
        t,
        'telegramSettings.deliverySection',
        transportDraft.rateLimitPerChatPerMin,
        1,
      )
    : null;
  const deliveryInvalid = Boolean(
    maxRetriesError || retryBackoffError || sendTimeoutError || rateLimitError,
  );

  // Write-only credential candidates — deliberately NOT part of `channelForm.draft` so a 409
  // recovery (which refetches and re-seeds the form) never discards what the operator typed
  // (design-channel-connection-pages.md §5.2/§7).
  const [botTokenCandidate, setBotTokenCandidate] = useState('');
  const [webhookSecretCandidate, setWebhookSecretCandidate] = useState('');

  const { feedback, success, error: notifyError, clear } = useFeedback();

  const connectionDirty =
    channelForm.dirty || botTokenCandidate.trim() !== '' || webhookSecretCandidate.trim() !== '';
  const dirty = connectionDirty || transportForm.dirty;

  // Save sequence (design-channel-connection-pages.md §7): connection, then delivery — stop at
  // the first failure, keep the other draft dirty so nothing already typed is lost.
  const saveConnection = () =>
    new Promise<void>((resolve, reject) => {
      const draft = channelForm.draft;
      if (!draft) {
        resolve();
        return;
      }
      channelUpdate.mutate(
        buildTelegramChannelUpdate(draft, botTokenCandidate, webhookSecretCandidate),
        {
          onSuccess: () => {
            setBotTokenCandidate('');
            setWebhookSecretCandidate('');
            resolve();
          },
          onError: (error) => {
            notifyError(error.message);
            if (error.status === 409) void channelQuery.refetch();
            reject(error);
          },
        },
      );
    });

  const saveDelivery = () =>
    new Promise<void>((resolve, reject) => {
      const draft = transportForm.draft;
      if (!draft) {
        resolve();
        return;
      }
      transportUpdate.mutate(toSettingsUpdate(draft), {
        onSuccess: () => resolve(),
        onError: (error) => {
          notifyError(error.message);
          if (error.status === 409) void transportQuery.refetch();
          reject(error);
        },
      });
    });

  const handleSave = () => {
    if (!dirty) return;
    void (async () => {
      try {
        if (connectionDirty) await saveConnection();
        if (transportForm.dirty) await saveDelivery();
        success(t('telegramSettings.savedFeedback'));
      } catch {
        // Already surfaced via `notifyError` inside each step.
      }
    })();
  };

  const handleResetConnectionAndDelivery = () => {
    channelForm.reset();
    transportForm.reset();
    setBotTokenCandidate('');
    setWebhookSecretCandidate('');
  };

  return (
    <SettingsScaffold
      title={t('telegramSettings.title')}
      subtitle={t('telegramSettings.subtitle')}
      icon={<TelegramIcon />}
      query={channelQuery}
      canView={canView}
      canManage={canManage}
      audit={channelQuery.data}
      dirty={dirty}
      saving={channelUpdate.isPending || transportUpdate.isPending}
      onSave={handleSave}
      onReset={handleResetConnectionAndDelivery}
      saveDisabled={deliveryInvalid}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const channelDraft = channelForm.draft;
        if (!channelDraft) return null;
        const channelDisabled = !canManage;
        const credentialDisabled = channelDisabled || !canManageCredentials;

        return (
          <Stack spacing={2.5}>
            <MovedSettingsNotice
              channel="telegram"
              canViewChatbot={chatbotPermissions.canView}
              canViewTemplates={templatesPermissions.canView}
              canViewChannels={channelsPermissions.canView}
              canViewTenant={tenantPermissions.canView}
            />

            {!canManage && canView ? (
              <Alert severity="info" icon={<LockOutlinedIcon fontSize="inherit" />}>
                {t('scaffold.readOnlyNotice')}
              </Alert>
            ) : null}
            {canManage && !canManageCredentials ? (
              <Alert severity="info">
                {t('telegramSettings.channelSection.credentialsRestrictedNotice')}
              </Alert>
            ) : null}

            <SettingsCard
              title={t('telegramSettings.botConnectionSection.title')}
              description={t('telegramSettings.botConnectionSection.description')}
              icon={<SmartToyRoundedIcon />}
            >
              <TextInputField
                label={t('telegramSettings.botIdentitySection.botCodeLabel')}
                description={t('telegramSettings.botIdentitySection.botCodeDescription')}
                value={channelDraft.botCode ?? ''}
                onChange={(value) => channelForm.setField('botCode', value)}
                disabled={channelDisabled}
              />
              <TextInputField
                label={t('telegramSettings.botIdentitySection.botUsernameLabel')}
                description={t('telegramSettings.botIdentitySection.botUsernameDescription')}
                value={channelDraft.botUsername ?? ''}
                onChange={(value) => channelForm.setField('botUsername', value)}
                disabled={channelDisabled}
              />
              <TextInputField
                label={t('telegramSettings.botIdentitySection.botNameLabel')}
                description={t('telegramSettings.botIdentitySection.botNameDescription')}
                value={channelDraft.botName ?? ''}
                onChange={(value) => channelForm.setField('botName', value)}
                disabled={channelDisabled}
              />
              <SwitchField
                label={t('telegramSettings.botIdentitySection.botEnabledLabel')}
                description={t('telegramSettings.botConnectionSection.botEnabledDescription')}
                checked={channelDraft.botEnabled}
                onChange={(value) => channelForm.setField('botEnabled', value)}
                disabled={channelDisabled}
              />
              <TextInputField
                label={t('telegramSettings.channelSection.miniAppUrlLabel')}
                description={t('telegramSettings.channelSection.miniAppUrlDescription')}
                value={channelDraft.miniAppUrl ?? ''}
                onChange={(value) => channelForm.setField('miniAppUrl', value)}
                disabled={channelDisabled}
                type="url"
              />
            </SettingsCard>

            <SettingsCard
              title={t('telegramSettings.credentialsSection.title')}
              description={t('telegramSettings.credentialsSection.description')}
              icon={<SecurityRoundedIcon />}
            >
              {channelDraft.botEnabled && !channelDraft.botSecretConfigured ? (
                <Alert
                  severity="warning"
                  icon={<KeyRoundedIcon fontSize="inherit" />}
                  sx={{ mb: 1 }}
                >
                  {t('telegramSettings.credentialsSection.noTokenWarning')}
                </Alert>
              ) : null}
              <ConfiguredSecretField
                label={t('telegramSettings.credentialsSection.botTokenLabel')}
                description={t('telegramSettings.credentialsSection.botTokenDescription')}
                configured={channelDraft.botSecretConfigured}
                secret={botTokenCandidate}
                onSecretChange={setBotTokenCandidate}
                disabled={credentialDisabled}
              />
              <ConfiguredSecretField
                label={t('telegramSettings.credentialsSection.webhookSecretLabel')}
                description={t('telegramSettings.credentialsSection.webhookSecretDescription')}
                configured={undefined}
                secret={webhookSecretCandidate}
                onSecretChange={setWebhookSecretCandidate}
                disabled={credentialDisabled}
                optional
              />
              {canManageCredentials ? (
                <FieldRow
                  label={t('telegramSettings.connectionSection.testLabel')}
                  description={t('telegramSettings.connectionSection.testDescription')}
                  alignTop
                >
                  <TestConnectionButton run={() => testTelegramConnection()} />
                </FieldRow>
              ) : null}
            </SettingsCard>

            {transportQuery.isLoading ? (
              <LoadingState
                label={t('telegramSettings.deliveryFormattingSection.loading')}
                fullHeight={false}
              />
            ) : null}

            {transportQuery.isError ? (
              <Alert
                severity="error"
                variant="outlined"
                action={
                  <Button size="small" onClick={() => void transportQuery.refetch()}>
                    {t('telegramSettings.retry')}
                  </Button>
                }
              >
                {t('telegramSettings.channelLoadError')}
              </Alert>
            ) : null}

            {transportForm.draft ? (
              <SettingsCard
                title={t('telegramSettings.deliveryFormattingSection.title')}
                icon={<SpeedRoundedIcon />}
                action={
                  transportQuery.data ? (
                    <Stack direction="row" spacing={1}>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t('scaffold.versionChip', { version: transportQuery.data.version })}
                      />
                    </Stack>
                  ) : undefined
                }
              >
                <SelectField
                  label={t('telegramSettings.formattingSection.parseModeLabel')}
                  description={t('telegramSettings.formattingSection.parseModeDescription')}
                  value={transportForm.draft.defaultParseMode ?? ''}
                  options={PARSE_MODE_OPTIONS}
                  onChange={(value) =>
                    transportForm.setField('defaultParseMode', value as TelegramParseMode)
                  }
                  disabled={channelDisabled}
                />
                <SwitchField
                  label={t('telegramSettings.formattingSection.disableWebPagePreviewLabel')}
                  description={t(
                    'telegramSettings.formattingSection.disableWebPagePreviewDescription',
                  )}
                  checked={transportForm.draft.disableWebPagePreview}
                  onChange={(value) => transportForm.setField('disableWebPagePreview', value)}
                  disabled={channelDisabled}
                />
                <NumberField
                  label={t('telegramSettings.deliverySection.maxRetriesLabel')}
                  description={t('telegramSettings.deliverySection.maxRetriesDescription')}
                  value={transportForm.draft.deliveryMaxRetries}
                  onChange={(value) => transportForm.setField('deliveryMaxRetries', value)}
                  disabled={channelDisabled}
                  min={0}
                  max={10}
                  error={Boolean(maxRetriesError)}
                  helperText={maxRetriesError}
                />
                <NumberField
                  label={t('telegramSettings.deliverySection.retryBackoffLabel')}
                  description={t('telegramSettings.deliverySection.retryBackoffDescription')}
                  value={transportForm.draft.deliveryRetryBackoffMs}
                  onChange={(value) => transportForm.setField('deliveryRetryBackoffMs', value)}
                  disabled={channelDisabled}
                  min={0}
                  suffix={t('telegramSettings.deliverySection.retryBackoffSuffix')}
                  error={Boolean(retryBackoffError)}
                  helperText={retryBackoffError}
                />
                <NumberField
                  label={t('telegramSettings.deliverySection.sendTimeoutLabel')}
                  description={t('telegramSettings.deliverySection.sendTimeoutDescription')}
                  value={transportForm.draft.sendTimeoutMs}
                  onChange={(value) => transportForm.setField('sendTimeoutMs', value)}
                  disabled={channelDisabled}
                  min={100}
                  max={120000}
                  suffix={t('telegramSettings.deliverySection.sendTimeoutSuffix')}
                  error={Boolean(sendTimeoutError)}
                  helperText={sendTimeoutError}
                />
                <NumberField
                  label={t('telegramSettings.deliverySection.rateLimitLabel')}
                  description={t('telegramSettings.deliverySection.rateLimitDescription')}
                  value={transportForm.draft.rateLimitPerChatPerMin}
                  onChange={(value) => transportForm.setField('rateLimitPerChatPerMin', value)}
                  disabled={channelDisabled}
                  min={1}
                  suffix={t('telegramSettings.deliverySection.rateLimitSuffix')}
                  error={Boolean(rateLimitError)}
                  helperText={rateLimitError}
                />
              </SettingsCard>
            ) : null}
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
